'use client'

import React, { useMemo, useRef } from 'react'
import { Carousel } from '@mantine/carousel'
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react'
import '@mantine/carousel/styles.css'
import { useIsMobile } from '@/lib/mobileDetector'
import ServiceItemCard from '@/components/ServiceItemCard'
import SpecialistCard from '@/components/SpecialistCard'
import HealthCard, { HealthCardData } from '@/components/HealthCard'
import { ServiceItem } from '@/types/interfaces/service-item'
import { ServiceItemCardType } from '@/types/enums/ServiceItem'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import { PageLocation, StoreAnalytics, trackStoreEvent } from '@nile/mantine-theme/analytics'

const StoreCarousel: React.FC<{
    services?: ServiceItem[]
    healthData?: HealthCardData[]
    cardType: ServiceItemCardType
    showDescription?: boolean
    pageLocation?: PageLocation
    additionalInformation?: Record<string, any>
}> = ({ services, healthData, cardType, showDescription, pageLocation, additionalInformation }) => {
    const arrowClickedRef = useRef(false)
    const wheelScrolledRef = useRef(false)
    const dragScrolledRef = useRef(false)
    const isMobile = useIsMobile()
    const IMAGE_HEIGHT = 85 // px – height of the slide image
    
    const itemCount = cardType === ServiceItemCardType.healthHistory 
        ? (healthData?.length ?? 0) 
        : (services?.length ?? 0)
    
    // For carousels with 4 or fewer items, scroll 1 at a time to avoid jumping to the end
    const scrollAmount = isMobile ? 1 : itemCount <= 4 ? 1 : 4
    
    // Hide controls when all items fit on screen at desktop size (4 items at 25% = 100%)
    const shouldShowControls = !isMobile && itemCount > 4
    // Create the plugin instance once; avoids reallocations on every re-render
    const wheelGestures = useMemo(
        () =>
            WheelGesturesPlugin({
                forceWheelAxis: 'x', // convert only vertical motion
                // @ts-expect-error `releaseWheel` exists at runtime but is missing from the type definition
                releaseWheel: true, // stops capturing once you hit either end
            }),
        [],
    )

    return (
        <Carousel
            plugins={[wheelGestures]}
            speed={10}
            classNames={{ control: 'carousel-control' }}
            withControls={shouldShowControls}
            previousControlProps={{ 'aria-label': 'Previous items' }}
            nextControlProps={{ 'aria-label': 'Next items' }}
            onWheelCapture={() => {
                // reset drag flag when wheel scroll starts
                dragScrolledRef.current = false
                wheelScrolledRef.current = true
            }}
            onPointerDownCapture={() => {
                // reset wheel flag when user begins dragging
                wheelScrolledRef.current = false
                dragScrolledRef.current = true
            }}
            slideSize={{ base: '45%', sm: '33.33%', md: '25%' }}
            slideGap={'md'}
            align="start"
            loop={false}
            containScroll="trimSnaps"
            dragFree
            slidesToScroll={scrollAmount}
            onSlideChange={handleSlideChange}
            nextControlIcon={
                <IconChevronRight
                    size={24}
                    onClick={() => {
                        arrowClickedRef.current = true
                        trackCarouselEvent('arrow', 'next')
                    }}
                />
            }
            previousControlIcon={
                <IconChevronLeft
                    size={24}
                    onClick={() => {
                        arrowClickedRef.current = true
                        trackCarouselEvent('arrow', 'prev')
                    }}
                />
            }
            controlsOffset="xs"
            styles={{
                control: {
                    opacity: 100,
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                },
                controls: {
                    top: IMAGE_HEIGHT,
                    justifyContent: 'space-between',
                    padding: '0',
                },
                viewport: {
                    padding: 0,
                    ...(isMobile && {
                        overflow: 'visible',
                        scrollSnapType: 'x mandatory',
                        WebkitOverflowScrolling: 'touch',
                        scrollPadding: '0 16px',
                    }),
                },
            }}
        >
            {cardType === ServiceItemCardType.healthHistory ? (
                healthData?.map((data, index) => (
                    <Carousel.Slide
                        key={`${data.type}-${index}`}
                        style={{
                            scrollSnapAlign: 'start',
                        }}
                    >
                        <HealthCard data={data} />
                    </Carousel.Slide>
                ))
            ) : (
                services?.map((service, index) => (
                    <Carousel.Slide
                        key={`${service.id}-${index}`}
                        style={{
                            scrollSnapAlign: 'start',
                            ...(pageLocation === PageLocation.GENERAL_AI_CHAT && {
                                height: '17rem',
                            }),
                        }}
                    >
                        {cardType === ServiceItemCardType.specialist ? (
                            <SpecialistCard
                                service={service}
                                showDescription={showDescription}
                                pageLocation={pageLocation!}
                                additionalInformation={additionalInformation}
                            />
                        ) : (
                            <ServiceItemCard
                                service={service}
                                showDescription={showDescription}
                                pageLocation={pageLocation!}
                                additionalInformation={additionalInformation}
                            />
                        )}
                    </Carousel.Slide>
                ))
            )}
        </Carousel>
    )

    function handleSlideChange() {
        if (arrowClickedRef.current) {
            arrowClickedRef.current = false
            return
        }
        if (wheelScrolledRef.current) {
            wheelScrolledRef.current = false
            trackCarouselEvent('wheel')
        } else if (dragScrolledRef.current) {
            dragScrolledRef.current = false
            trackCarouselEvent('drag')
        }
    }

    function trackCarouselEvent(method: string, direction?: string) {
        trackStoreEvent(StoreAnalytics.SCROLLED_CAROUSEL, {
            method,
            direction,
            location: pageLocation,
            additionalInformation,
        })
    }
}

export default StoreCarousel
